import { Fullscript } from '@fullscript/fullscript-js'
import { ENV } from '@/config'

/**
 * Loader + thin wrapper around `@fullscript/fullscript-js`.
 *
 * The SDK is shipped as an npm package (NOT a CDN) and is initialised
 * once per app with a publicKey + env. Subsequent `create('platform', …)`
 * calls reuse the same client instance.
 *
 * Shape per Fullscript's published types:
 *   const client = Fullscript({ publicKey, env: 'us-snd' })
 *   const feature = client.create('platform', { secretToken, entrypoint: 'labs' })
 *   await feature.mount('my-container-id')   // element id, not element ref
 *   feature.unmount()
 *
 * NOTE: the package only re-exports a subset of its types from the
 * top-level barrel — `Client`, `Feature`, `PlatformOptions` are
 * referenced internally but not exported. We declare narrow local types
 * matching their actual runtime shape so consumers don't reach into
 * `dist/`-internal paths (which can break across patch releases).
 */

/** Env values accepted by the SDK factory — sandboxed per region.
 *  `dev` is for Fullscript-internal use; we don't expose it. */
export type FullscriptEnv = 'us' | 'ca' | 'us-snd' | 'ca-snd'

/** Patient identity payload. Either `id` (cached fullscript patient id)
 *  OR demographics; not both. */
export interface FullscriptPatientOptions {
  id?: string
  firstName?: string
  lastName?: string
  email?: string
  mobileNumber?: string
  dateOfBirth?: string
  gender?: 'male' | 'female' | 'prefer not to say'
  biologicalSex?: 'male' | 'female' | 'prefer not to say'
  discount?: number
}

export interface FullscriptPlatformOptions {
  secretToken: string
  entrypoint?: 'labs' | 'catalog'
  patient?: FullscriptPatientOptions
}

export interface FullscriptFeature {
  mount: (elementId: string) => Promise<void>
  unmount: () => void
  on?: (event: string, handler: (payload: unknown) => void) => void
  off?: (event: string, handler: (payload: unknown) => void) => void
}

export interface FullscriptClient {
  create: (
    feature: 'platform' | 'treatmentPlan',
    options: FullscriptPlatformOptions,
  ) => FullscriptFeature
}

/**
 * Fullscript's patient-facing storefront for the given SDK env — sandbox
 * envs live on `fullscript.io`, production on `fullscript.com`. Patients
 * buy there directly (deep-link in a new tab); Fullscript does not support
 * a patient-scoped embed inside our app.
 */
export const fullscriptPatientSiteUrl = (env: FullscriptEnv): string =>
  env.endsWith('-snd') ? `https://${env}.fullscript.io/` : `https://${env}.fullscript.com/`

let cachedClient: FullscriptClient | null = null

/**
 * Returns a singleton client built from `ENV.FULLSCRIPT.{PUBLIC_KEY,ENV}`.
 *
 * Throws synchronously if the publicKey is missing — that's an
 * ops/config bug we want to surface loudly during dev rather than
 * silently failing at mount time.
 */
export const getFullscriptClient = (): FullscriptClient => {
  if (cachedClient) return cachedClient

  const { PUBLIC_KEY, ENV: fsEnv } = ENV.FULLSCRIPT
  if (!PUBLIC_KEY) {
    throw new Error(
      'VITE_FULLSCRIPT_PUBLIC_KEY is not set. Get the publicKey from the Fullscript Integrate dashboard and add it to .env.development (or your deploy env).',
    )
  }

  // SDK's published types are slightly off from the runtime — cast at the
  // boundary so downstream consumers see clean shapes.
  cachedClient = Fullscript({
    publicKey: PUBLIC_KEY,
    env: fsEnv,
  }) as unknown as FullscriptClient
  return cachedClient
}

/** Test hook — drops the cached client so specs can re-init. */
export const __resetFullscriptClientForTests = () => {
  cachedClient = null
}
